/**
 * CurrentFactorField.jsx — PROD-1154 (patient half). The "current verification code" field that
 * "Change verification method" (TwoFactorSetup), "Regenerate backup codes" (Security / Two-factor screens)
 * and a move of the SMS sign-in number (OnboardingPhone) show while 2FA is on, the "Text me a code" action
 * for SMS accounts, and the sign-in lockout notice. Rules: utils/currentFactor.js. Mirrors the md panel's
 * components/auth/CurrentFactorField.jsx; every word is an existing key except the lock body.
 *
 * The send uses POST /auth/2fa/send-current-code, which answers like /auth/2fa/send-sms: smsSent:false is
 * not a sent code, and sms_us_ca_only shows the server's sentence (PROD-807). Every caller renders this field
 * only after the API asked (403 2fa_current_factor_required), so the route exists wherever it is offered; any
 * other failure, a 404 included, is the plain send-failed sentence.
 * The field always carries the API's explanation (security.currentCodeRequired) above it, so it is never taken
 * for the code of a NEW method or number.
 */
import { useState, useEffect, useCallback } from 'react'
import { authAPI } from '../services/api'
import { useI18n } from '../i18n'
import { isSmsRefused, smsRefusedSentence } from '../utils/smsRefusal'
import { currentCodeInput, lockMessage } from '../utils/currentFactor'

/** [seconds | null, start(seconds)]: a one-second countdown for the lockout notice. */
export function useLockCountdown() {
  const [seconds, setSeconds] = useState(null)
  useEffect(() => {
    if (!seconds) return undefined
    const id = setTimeout(() => setSeconds((s) => (s && s > 1 ? s - 1 : null)), 1000)
    return () => clearTimeout(id)
  }, [seconds])
  const start = useCallback((s) => setSeconds(Number(s) > 0 ? Math.ceil(Number(s)) : null), [])
  return [seconds, start]
}

export function LockNotice({ seconds }) {
  const { t } = useI18n()
  if (!seconds) return null
  return (
    <div className="mb-3 p-3.5 bg-red-50 border border-red-200 rounded-xl text-left w-full" data-2fa-lock="1">
      <p className="text-red-700 font-medium text-sm">{t('signIn.accountTemporarilyLocked')}</p>
      <p className="text-red-600 text-sm mt-1">{lockMessage(t, seconds)}</p>
    </div>
  )
}

/**
 * The code field. `method` is the account's CURRENT 2FA method ('sms' | 'totp'); the send action is
 * offered only for 'sms' (the route refuses an authenticator account).
 */
export default function CurrentFactorField({ value, onChange, method, disabled = false, autoFocus = false, onEnter }) {
  const { t } = useI18n()
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [sendError, setSendError] = useState('')

  const sendCode = async () => {
    setSending(true)
    setSent(false)
    setSendError('')
    try {
      const response = await authAPI.sendCurrent2faCode()
      if (response && response.smsSent === false) setSendError(t('agent.messengerPrep.sendFailed', "Couldn't send. Try again in a moment."))
      else setSent(true)
    } catch (err) {
      if (isSmsRefused(err) && smsRefusedSentence(err)) setSendError(smsRefusedSentence(err))
      else setSendError(t('agent.messengerPrep.sendFailed', "Couldn't send. Try again in a moment."))
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="text-left w-full" data-2fa-current-code="1">
      <p className="text-xs text-gray-600 mb-2" data-2fa-current-explain="1">
        {t('security.currentCodeRequired', 'Two-factor authentication is already on. Enter a current verification code to change it.')}
      </p>
      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="current-2fa-code">
        {t('ssoCode.title', 'Enter your verification code')}
      </label>
      {sent && !sendError && (
        <p className="text-xs text-gray-500 mb-2">{t('signIn.verificationCodeSent', 'Verification code sent to your phone. Enter it below.')}</p>
      )}
      {sendError && <p role="alert" className="text-xs text-red-600 mb-2">{sendError}</p>}
      <div className="flex items-center gap-2">
        <input
          id="current-2fa-code"
          type="text"
          autoComplete="one-time-code"
          autoCapitalize="off"
          spellCheck={false}
          value={value}
          onChange={(e) => onChange(currentCodeInput(e.target.value))}
          onKeyDown={(e) => { if (e.key === 'Enter' && onEnter) { e.preventDefault(); onEnter() } }}
          maxLength={8}
          disabled={disabled}
          autoFocus={autoFocus}
          className="flex-1 min-w-0 px-4 py-3 rounded-xl border-2 border-gray-200 bg-white text-base font-mono tracking-[0.2em] text-center text-gray-800 focus:outline-none focus:border-orella-500 disabled:opacity-50"
        />
        {method === 'sms' && (
          <button
            type="button"
            onClick={sendCode}
            disabled={disabled || sending}
            className="px-4 py-3 text-sm font-medium rounded-xl border border-gray-200 text-orella-500 bg-white disabled:opacity-50 whitespace-nowrap"
          >
            {sending ? t('common.sending', 'Sending...') : t('onboarding.phone.send', 'Text me a code')}
          </button>
        )}
      </div>
    </div>
  )
}
